import React, { FC } from 'react';
import styles from './StepLayout.scss';
import { Box, Button, ButtonProps, Loader, TextButton } from '@wix/design-system';
import { ChevronLeft } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import { useOrchestratorNavigation, useOrchestratorState } from '@wix/gg-common-lib/orchestrator';
import { StepParams } from 'types/steps';
import { biEvents } from 'api/bi-events';
import { BI_ACTION_NAME, BI_STEP_NAME } from 'config/bi-events';
import { StepId } from 'config/flow';

export type StepLayoutActionsProps = Props;

type Props = {
  onNext?: () => void;
  shouldSkip?: boolean;
  overrideNext?: (next: () => void) => void;
  nextButtonProps?: Pick<ButtonProps, 'disabled'> & {
    label: string;
    isLoading?: boolean;
  };
};

export const StepLayoutActions: FC<Props> = ({ onNext, nextButtonProps, shouldSkip, overrideNext }) => {
  const { t } = useTranslation();
  const { next, back, canGoBack, canGoNext, skip } = useOrchestratorNavigation();
  const { currentStep } = useOrchestratorState<StepParams>();

  const handlePrimaryButtonClick = () => {
    const nextFn = () => {
      onNext?.();
      return next();
    };
    return overrideNext?.(nextFn) ?? nextFn();
  };

  const handleBackButtonClick = () => {
    if (currentStep?.id) {
      biEvents.actionClicked({
        screenName: BI_STEP_NAME[currentStep.id as StepId],
        action: BI_ACTION_NAME.BACK,
      });
    }
    back();
  };

  const handleSkipButtonClick = () => {
    if (currentStep?.id) {
      biEvents.actionClicked({
        screenName: BI_STEP_NAME[currentStep.id as StepId],
        action: BI_ACTION_NAME.SKIP,
      });
    }
    skip();
  };

  return (
    <div className={styles.stepLayoutActions}>
      <div className={styles.stepLayoutActionsContent}>
        {canGoBack && !currentStep?.params?.disableBack ? (
          <TextButton skin="dark" prefixIcon={<ChevronLeft />} onClick={handleBackButtonClick}>
            {t('actions-bar.button.back')}
          </TextButton>
        ) : (
          <div />
        )}
        <div className={styles.stepLayoutActionsForward}>
          {shouldSkip && canGoNext && (
            <TextButton skin="dark" onClick={handleSkipButtonClick}>
              {t('actions-bar.button.skip')}
            </TextButton>
          )}
          {!!nextButtonProps && (
            <Button
              {...nextButtonProps}
              disabled={nextButtonProps.isLoading || nextButtonProps.disabled}
              onClick={handlePrimaryButtonClick}
              skin="dark"
            >
              <Box verticalAlign="middle" gap="10px">
                {nextButtonProps.isLoading && <Loader size="tiny" color="white" />}
                {nextButtonProps.label}
              </Box>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
